import { request } from '@/lib/api'

/**
 * Задачи, предложенные ИИ по письмам, звонкам и комментариям (тикет #131).
 *
 * Поля ответа сравниваются нестрого (`== null`): сервер не кладёт в JSON пустые поля вовсе
 * (`non_null`), и `undefined` здесь — обычное «не заполнено».
 */

export interface ProposalSource {
  type: 'activity' | 'comment'
  id: string
  /** `email`, `call` или `comment`. */
  kind: 'email' | 'call' | 'comment'
  direction?: 'inbound' | 'outbound' | null
  at: string
}

/** Кого можно поставить исполнителем вместо предложенного. */
export interface ProposalChoice {
  employeeId: string
  name: string
  /** `owner` — ответственный, `history` — работал с клиентом, `involved` — участвовал, `department` — коллега. */
  role: 'owner' | 'history' | 'involved' | 'department'
  load: number
  history: number
  onLeaveUntil?: string | null
  /** Смотрящий вправе поставить его исполнителем. */
  assignable: boolean
}

export interface TaskProposal {
  id: string
  status: 'proposed' | 'accepted' | 'dismissed'
  /** `do` — сделать нам, `follow_up` — проконтролировать обещание клиента. */
  kind: 'do' | 'follow_up'
  subject: string
  basis: string
  /** Цитата найдена в источнике дословно. */
  basisVerified: boolean
  source: ProposalSource
  entityType?: string | null
  entityId?: string | null
  counterpartyId?: string | null
  counterpartyName?: string | null
  dueText?: string | null
  dueDate?: string | null
  dueAt?: string | null
  /** Почему срока нет — словами. */
  dueNote?: string | null
  ownerEmployeeId?: string | null
  ownerName?: string | null
  assigneeEmployeeId?: string | null
  assigneeName?: string | null
  assigneeReason?: string | null
  alternatives?: ProposalChoice[] | null
  /** Подтвердить как предложено (одно нажатие) смотрящий вправе. */
  assignable: boolean
  taskId?: string | null
  createdAt: string
}

export interface MyProposals {
  hasEmployeeCard: boolean
  proposals: TaskProposal[]
}

export interface ProposalChanges {
  subject?: string
  dueAt?: string
  assigneeEmployeeId?: string
}

export interface AcceptResult {
  taskId: string
  /** `false` — предложение уже было подтверждено, это его задача. */
  created: boolean
  proposal: TaskProposal
}

export function fetchMyProposals(signal?: AbortSignal): Promise<MyProposals> {
  return request<MyProposals>('/tasks/proposals', { signal })
}

export function fetchRecordProposals(entityType: string, entityId: string, signal?: AbortSignal): Promise<TaskProposal[]> {
  const query = new URLSearchParams({ entityType, entityId })
  return request<{ proposals: TaskProposal[] }>(`/tasks/proposals?${query.toString()}`, { signal })
    .then((answer) => answer.proposals)
}

export function acceptProposal(id: string, changes?: ProposalChanges): Promise<AcceptResult> {
  return request<AcceptResult>(`/tasks/proposals/${id}/accept`, { method: 'POST', body: changes ?? {} })
}

export function dismissProposal(id: string): Promise<TaskProposal> {
  return request<TaskProposal>(`/tasks/proposals/${id}/dismiss`, { method: 'POST' })
}

export function fetchTaskExtractionSwitch(signal?: AbortSignal): Promise<{ enabled: boolean }> {
  return request<{ enabled: boolean }>('/admin/integrations/ai-tasks', { signal })
}

export function saveTaskExtractionSwitch(enabled: boolean): Promise<{ enabled: boolean }> {
  return request<{ enabled: boolean }>('/admin/integrations/ai-tasks', { method: 'PUT', body: { enabled } })
}
